/**
 * SequencerGrid — N lanes × M steps step sequencer.
 *
 * Visual layout:
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ [▶ PLAY] · 16-step drums                          [×]        │  ← header
 *   ├─────────┬──┬──┬──┬──┬──┬──┬──┬──┬──┬──┬──┬──┬──┬──┬──┬──┬───┤
 *   │ Kick    │ ■│  │  │  │ ■│  │  │  │ ■│  │  │  │ ■│  │  │  │   │  ← lane 0
 *   ├─────────┼──┼──┼──┼──┼──┼──┼──┼──┼──┼──┼──┼──┼──┼──┼──┼──┼───┤
 *   │ Snare   │  │  │  │  │ ■│  │  │  │  │  │  │  │ ■│  │  │  │   │  ← lane 1
 *   ├─────────┼──┼──┼──┼──┼──┼──┼──┼──┼──┼──┼──┼──┼──┼──┼──┼──┼───┤
 *   │ CHat    │ ■│  │ ■│  │ ■│  │ ■│  │ ■│  │ ■│  │ ■│  │ ■│  │   │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Interactions:
 *   - Tap step: toggle on/off.
 *   - Drag vertical on an ON step: change velocity (1..127). Bar height
 *     visually maps to velocity so you can see your dynamics at a glance.
 *   - Tap lane label: audition the lane (one MIDI note at velocity 100).
 *   - Long-press lane label (500ms): open the lane editor.
 *   - Tap sequencer title (in edit mode): rename.
 *   - [▶ PLAY]: play / stop the sequencer. State follows bridge broadcasts.
 *
 * Visual feedback for "what's playing":
 *   Each step cell pulses (glow + scale) for ~220ms when the bridge
 *   reports it just fired. That's the only motion cue — no separate
 *   continuous-playhead column (it'd duplicate what the pulse already
 *   shows in a discrete-step instrument).
 */

import { createMemo, createEffect, createSignal, on, For, Show, type Accessor } from 'solid-js';
import type { SequencerItem } from '../layout/store.ts';
import { isEditMode, removeItem } from '../layout/store.ts';
import {
  findSequencer,
  toggleStep,
  setStepVelocity,
  sequencerLibrary,
  renameSequencer,
} from '../sequencer/store.ts';
import { sequencerStates } from '../sequencer/runtime.ts';
import { effectiveVelocity, isDmxLaneTarget, type ActionStep, type Sequencer } from '@opbuttons/schema';
import { describeLaneLight } from './LaneLightPicker.tsx';
import { anyBridgeConnectedReactive } from '../opbridge/store.ts';
import { pushSequencer, armSequencerAction, fireAction, fanoutTargets } from '../opbridge/dispatch.ts';
import { pokeEnginesStatePoll } from '../opbridge/enginesStatePoll.ts';
import { SequencerLaneEditor } from './SequencerLaneEditor.tsx';

interface SequencerGridProps {
  item: SequencerItem;
}

const LONG_PRESS_MS = 500;
const AUDITION_VELOCITY = 100;
const AUDITION_GATE_MS = 150;

export function SequencerGrid(props: SequencerGridProps) {
  // The library entry. Reactive — any toggle/edit re-renders.
  const seq = createMemo(() => {
    // Touch the library signal so reactivity propagates when the user
    // toggles a step or patches a lane. findSequencer pulls from the
    // same signal but the createMemo dependency must register on the
    // signal accessor for Solid to recompute.
    void sequencerLibrary();
    return findSequencer(props.item.sequencerId);
  });

  const snapshot = createMemo(() => sequencerStates()[props.item.sequencerId]);
  // Optimistic overlay so the PLAY button reacts on tap (the engine-state
  // poll is authoritative but arrives ~1s later). Cleared once a real
  // snapshot state lands for this sequencer.
  const [optimistic, setOptimistic] =
    createSignal<'idle' | 'armed' | 'running' | 'killing' | null>(null);
  const playState = createMemo(() => optimistic() ?? snapshot()?.state ?? 'idle');
  createEffect(on(
    () => snapshot()?.state,
    (real) => { if (real) setOptimistic(null); },
    { defer: true },
  ));

  // Lane editor — null when closed, otherwise the laneId currently being
  // edited. Opened by long-press on a lane label.
  const [editingLaneId, setEditingLaneId] = createSignal<string | null>(null);
  // Inline title rename. Holds the working string while editing.
  const [editingTitle, setEditingTitle] = createSignal<string | null>(null);

  function tapPlay() {
    if (!anyBridgeConnectedReactive(props.item.bridgeIds)) return;
    const s = seq();
    if (!s) return;
    // Optimistic flip: idle/killing → arming, otherwise heading to kill.
    const cur = playState();
    setOptimistic(cur === 'idle' || cur === 'killing' ? 'armed' : 'killing');
    void driveArm(s);
  }

  /** Push the current pattern to every configured bridge's engine
   *  (idempotent — covers live step edits), then toggle-arm it there
   *  (multi-select fan-out, 2026-08-13 — undefined bridgeIds resolves to
   *  the default bridge). Each target's push+arm runs in order against
   *  that target; targets run in parallel with each other. (Legacy
   *  Node-bridge `bridge.armSequencer` removed — bucket B migration to
   *  OPBridge, DEC-464 unblocked the library push scope.) */
  async function driveArm(s: Sequencer) {
    const perTarget = fanoutTargets(props.item.bridgeIds).map(async (target) => {
      const pushed = await pushSequencer(s, target);
      const pushErr = pushed.find((r) => r.kind !== 'ok');
      if (pushErr) {
        console.warn(`[SequencerGrid] pushSequencer → ${pushErr.target}: ${pushErr.kind}${pushErr.detail ? ' - ' + pushErr.detail : ''}`);
        return pushErr;
      }
      const armed = await armSequencerAction(s.id, target);
      const armErr = armed.find((r) => r.kind !== 'ok');
      if (armErr) {
        console.warn(`[SequencerGrid] armSequencer → ${armErr.target}: ${armErr.kind}${armErr.detail ? ' - ' + armErr.detail : ''}`);
        return armErr;
      }
      return { target, kind: 'ok' } as FireResult;
    });
    const results = await Promise.all(perTarget);
    reportSend(props.item.id, controlName(props.item, 'Sequencer'), results);
    if (!results.some((r) => r.kind === 'ok')) { setOptimistic(null); return; }
    // Nudge the engine-state poll so the authoritative state + step pulses
    // appear ~150ms after arm instead of waiting out the idle cadence.
    pokeEnginesStatePoll();
  }

  function onRemove() {
    removeItem(props.item.id);
  }

  function startTitleEdit() {
    const s = seq();
    if (!s) return;
    setEditingTitle(s.name);
  }
  function commitTitleEdit() {
    const v = editingTitle();
    if (v !== null) renameSequencer(props.item.sequencerId, v);
    setEditingTitle(null);
  }
  function cancelTitleEdit() {
    setEditingTitle(null);
  }

  return (
    <div
      class="seq"
      data-arm-state={playState()}
    >
      <header class="seq-head">
        <button
          class="seq-arm"
          type="button"
          data-state={playState()}
          onPointerDown={(e) => { e.stopPropagation(); tapPlay(); }}
          disabled={!anyBridgeConnectedReactive(props.item.bridgeIds)}
        >
          <span class="seq-arm-glyph">
            {playState() === 'running' ? '■' : playState() === 'armed' ? '◐' : '▶'}
          </span>
          <span class="seq-arm-label">
            {playState() === 'running' ? 'STOP'
              : playState() === 'armed' ? 'WAIT'
              : playState() === 'killing' ? 'KILL'
              : 'PLAY'}
          </span>
        </button>
        <Show
          when={editingTitle() !== null}
          fallback={
            <div
              class="seq-title"
              role="button"
              tabIndex={0}
              title="Click to rename"
              onClick={startTitleEdit}
              onKeyDown={(e) => { if (e.key === 'Enter') startTitleEdit(); }}
            >
              {seq()?.name ?? 'Sequencer (missing)'}
            </div>
          }
        >
          <input
            class="seq-title seq-title-input"
            type="text"
            value={editingTitle() ?? ''}
            maxLength={32}
            placeholder="sequencer"
            aria-label="Sequencer name"
            ref={(el) => setTimeout(() => el.focus(), 0)}
            onInput={(e) => setEditingTitle(e.currentTarget.value)}
            onBlur={commitTitleEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitTitleEdit(); }
              if (e.key === 'Escape') { e.preventDefault(); cancelTitleEdit(); }
            }}
          />
        </Show>
        {isEditMode() && (
          <button
            class="seq-remove"
            type="button"
            onPointerDown={(e) => { e.stopPropagation(); onRemove(); }}
            title="Remove this sequencer item (the pattern stays in the library)"
            aria-label="Remove"
          >
            ×
          </button>
        )}
      </header>

      <div class="seq-body">
        {seq() ? (
          <For each={seq()!.lanes}>
            {(lane, laneIx) => (
              <SequencerLaneView
                lane={lane}
                laneIx={laneIx}
                sequencerId={props.item.sequencerId}
                lengthSteps={seq()!.lengthSteps}
                snapshot={snapshot}
                bridgeIds={props.item.bridgeIds}
                onEditLane={() => setEditingLaneId(lane.id)}
              />
            )}
          </For>
        ) : (
          <div class="seq-missing">
            Sequencer document not found. Remove this item and add a fresh one.
          </div>
        )}
      </div>

      <Show when={editingLaneId() && seq()}>
        <SequencerLaneEditor
          sequencerId={props.item.sequencerId}
          laneId={editingLaneId()!}
          onClose={() => setEditingLaneId(null)}
        />
      </Show>
    </div>
  );
}

// ====================================================================
// Per-lane view — step buttons. No playhead overlay.
// ====================================================================

import type { SequencerLane } from '@opbuttons/schema';
import type { SequencerSnapshot } from '../sequencer/runtime.ts';
import { reportSend, controlName } from '../ui/pressReport.ts';
import type { FireResult } from '../opbridge/dispatch.ts';

interface SequencerLaneProps {
  lane: SequencerLane;
  laneIx: Accessor<number>;
  sequencerId: string;
  lengthSteps: number;
  snapshot: Accessor<SequencerSnapshot | undefined>;
  /** The owning SequencerItem's bridge affinity (multi-select, 2026-08-13)
   *  — audition fires through the same target(s) the sequencer itself
   *  dispatches to. */
  bridgeIds: string[] | undefined;
  onEditLane: () => void;
}

function SequencerLaneView(props: SequencerLaneProps) {
  // Tap/long-press detection on the lane label.
  //   Short tap (<500ms, no drag): audition — fire one MIDI note at v=100.
  //   Long press (>=500ms): open the editor.
  // We distinguish by setting a timer on pointerdown and seeing if pointerup
  // arrives before it fires.
  let longPressTimer: ReturnType<typeof setTimeout> | null = null;
  let downX = 0;
  let downY = 0;
  let dragged = false;
  let firedLongPress = false;

  function audition() {
    if (!anyBridgeConnectedReactive(props.bridgeIds)) return;
    // A light lane has no audition: OPBridge's one-shot fixture write
    // (`sendDmxState`) covers colour / level / power only, so a tap would
    // "audition" some capabilities and silently not others. Arm the
    // sequencer to see it. The tooltip says so.
    const t = props.lane.target;
    if (isDmxLaneTarget(t)) return;
    const { kind, number, channel } = t;
    // Fire through OPBridge's dispatcher (the note step schedules its own
    // note-off after AUDITION_GATE_MS). Lane channel is 0-based; ActionStep
    // channel is 1-16. (Legacy `bridge.sendMidi` removed — bucket B.) Fans
    // out to every configured target, same as the sequencer's own dispatch.
    for (const target of fanoutTargets(props.bridgeIds)) {
      const step: ActionStep = kind === 'cc'
        ? { id: 'seq-audition', target, type: 'midi.cc', channel: channel + 1, cc: number, value: AUDITION_VELOCITY }
        : { id: 'seq-audition', target, type: 'midi.note', channel: channel + 1, note: number, velocity: AUDITION_VELOCITY, duration: AUDITION_GATE_MS };
      void fireAction(step);
    }
  }

  function onLabelDown(e: PointerEvent) {
    e.stopPropagation();
    downX = e.clientX;
    downY = e.clientY;
    dragged = false;
    firedLongPress = false;
    longPressTimer = setTimeout(() => {
      firedLongPress = true;
      longPressTimer = null;
      props.onEditLane();
    }, LONG_PRESS_MS);
  }
  function onLabelMove(e: PointerEvent) {
    if (!longPressTimer && !dragged) return;
    if (Math.abs(e.clientX - downX) > 6 || Math.abs(e.clientY - downY) > 6) {
      dragged = true;
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
    }
  }
  function onLabelUp(e: PointerEvent) {
    e.stopPropagation();
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
    // Short tap (no drag, no long-press fire): audition the lane.
    if (!dragged && !firedLongPress) {
      audition();
    }
  }

  return (
    <div class="seq-lane">
      <button
        class="seq-lane-label"
        type="button"
        title={laneTitle(props.lane.target)}
        onPointerDown={onLabelDown}
        onPointerMove={onLabelMove}
        onPointerUp={onLabelUp}
        onPointerCancel={onLabelUp}
      >
        {props.lane.label ?? `Lane ${props.laneIx() + 1}`}
      </button>
      <div
        class="seq-lane-steps"
        style={{ '--seq-len': props.lengthSteps }}
      >
        <For each={props.lane.steps.slice(0, props.lengthSteps)}>
          {(step, stepIx) => (
            <StepCell
              step={step}
              stepIx={stepIx}
              lane={props.lane}
              sequencerId={props.sequencerId}
              snapshot={props.snapshot}
              laneIx={props.laneIx}
            />
          )}
        </For>
      </div>
    </div>
  );
}

// ====================================================================
// One step cell — toggle + velocity drag + pulse
// ====================================================================

const VELOCITY_DRAG_PX = 80;  // 80px = full 1..127 range

interface StepCellProps {
  step: import('@opbuttons/schema').SequencerStep;
  stepIx: Accessor<number>;
  lane: SequencerLane;
  sequencerId: string;
  laneIx: Accessor<number>;
  snapshot: Accessor<SequencerSnapshot | undefined>;
}

function StepCell(props: StepCellProps) {
  // Velocity drag tracking. Pointer-down captures the starting clientY +
  // starting velocity; pointermove updates velocity by inverse delta. On
  // a tap (no drag movement past ~3px) we toggle the step instead.
  let dragStartY = 0;
  let dragStartVel = 100;
  let dragged = false;
  let pointerId: number | null = null;

  // Pulse timestamp for animations. -1 = no recent fire. Updated when
  // the snapshot's lastFiredStep for this lane matches our step index AND
  // it changed since last render.
  let lastSeenFireStep = -1;
  let lastSeenFireSnapTs = 0;

  function onDown(e: PointerEvent) {
    e.stopPropagation();
    e.preventDefault();
    dragStartY = e.clientY;
    dragStartVel = effectiveVelocity(props.step);
    dragged = false;
    pointerId = e.pointerId;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onMove(e: PointerEvent) {
    if (pointerId !== e.pointerId) return;
    const dy = dragStartY - e.clientY; // up = positive
    if (Math.abs(dy) > 3) dragged = true;
    if (!dragged) return;
    if (!props.step.on) return; // only ON steps support velocity edit
    const next = dragStartVel + (dy / VELOCITY_DRAG_PX) * 126;
    setStepVelocity(
      props.sequencerId,
      props.lane.id,
      props.stepIx(),
      Math.max(1, Math.min(127, Math.round(next))),
    );
  }
  function onUp(e: PointerEvent) {
    if (pointerId !== e.pointerId) return;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    pointerId = null;
    if (dragged) return;
    // Tap: toggle. If currently off, default velocity is 100 (set by
    // makeBlankLane → blankStep, but explicit when toggling on).
    toggleStep(props.sequencerId, props.lane.id, props.stepIx());
  }

  // Fire-pulse: re-renders this cell when the snapshot's lastFiredStep
  // for our lane equals our step index AND the snapshot's wall-clock ts
  // is fresher than what we last logged. We don't manage a timer here —
  // CSS animation runs once when data-pulse-ts changes.
  const pulseTs = createMemo(() => {
    const snap = props.snapshot();
    if (!snap || snap.state !== 'running') return 0;
    const lf = snap.lastFiredSteps[props.laneIx()];
    if (lf !== props.stepIx()) return lastSeenFireSnapTs;
    if (lf === lastSeenFireStep && snap.ts === lastSeenFireSnapTs) {
      // Same fire event we've already animated. Don't restart.
      return lastSeenFireSnapTs;
    }
    lastSeenFireStep = lf;
    lastSeenFireSnapTs = snap.ts;
    return snap.ts;
  });

  // CSS reads --vel-pct as the bar-height of the velocity indicator.
  // ON steps: pct = velocity / 127. OFF steps: 0.
  const velPct = createMemo(() => {
    if (!props.step.on) return 0;
    return effectiveVelocity(props.step) / 127;
  });

  // Beat markers — every 4th step gets a thicker accent. Helps the eye
  // count to the downbeat without having to count individual steps.
  const isBeatBoundary = createMemo(() => props.stepIx() % 4 === 0);

  // Re-trigger CSS animation by toggling a key based on pulseTs. We
  // express the pulse via data-pulse-id so any change of the integer
  // restarts the keyframe (animation: none → animation: pulse).
  createEffect(() => { void pulseTs(); }); // ensure subscription

  return (
    <button
      class="seq-step"
      type="button"
      data-on={props.step.on ? 'true' : 'false'}
      data-beat={isBeatBoundary() ? 'true' : 'false'}
      data-pulse-id={pulseTs()}
      style={{ '--vel-pct': velPct() }}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      aria-label={`step ${props.stepIx() + 1} ${props.step.on ? 'on' : 'off'}`}
    >
      <span class="seq-step-bar" />
    </button>
  );
}

/** The lane label's tooltip: what it drives, and what a tap does. */
function laneTitle(t: SequencerLane['target']): string {
  if (isDmxLaneTarget(t)) {
    return `Light lane: ${describeLaneLight(t)} - arm the sequencer to see it · long-press to edit`;
  }
  return `Tap to audition · long-press to edit · note ${t.number} ch ${t.channel + 1}`;
}
